// Hook : flux de job-events CLI (frames PTY + events structurés).
//
// Le plugin streame via Event("live","cli","frame"|"event"|"exit",payload) →
// enveloppe `live/cli/<op>` (calquée live/process/watch.*, CONTRACTS §3).
// MergeJobPayload aplatit payload + {jobId,seq} au top-level de env.payload.
// `useJobEvents` (shared, live/plugin/job.event) ne convient pas → souscription directe.
import { createSignal, onCleanup } from "solid-js";
import { useWs } from "@/shared/ws";
import { isWsEnvelope } from "@/shared/ws-protocol";
import type { CliEvent, CliPtyFrame } from "./types";

export interface CliStream {
  events: () => CliEvent[];
  frames: () => CliPtyFrame[];
  exited: () => { exitCode: number } | null;
  reset: () => void;
  /** Pousse des events issus du replay (préfixe l'historique). */
  prepend: (evs: CliEvent[]) => void;
}

export function useCliJobStream(jobId: () => string | null): CliStream {
  const ws = useWs();
  const [events, setEvents] = createSignal<CliEvent[]>([]);
  const [frames, setFrames] = createSignal<CliPtyFrame[]>([]);
  const [exited, setExited] = createSignal<{ exitCode: number } | null>(null);

  const off = ws.on(
    (m) =>
      isWsEnvelope(m, "live", "cli", "frame") ||
      isWsEnvelope(m, "live", "cli", "event") ||
      isWsEnvelope(m, "live", "cli", "exit"),
    (m) => {
      const env = m as {
        op?: string;
        payload?: {
          jobId?: string;
          sessionId?: string;
          stream?: "stdout" | "stderr";
          dataB64?: string;
          kind?: string;
          exitCode?: number;
        };
      };
      const p = env.payload;
      if (!p || p.jobId !== jobId()) return;
      // op ∈ {frame, event, exit} ; payload aplati au top-level (MergeJobPayload)
      if (env.op === "frame") {
        if (typeof p.dataB64 === "string") {
          setFrames((prev) => [
            ...prev,
            { sessionId: p.sessionId ?? "", stream: p.stream ?? "stdout", dataB64: p.dataB64 as string },
          ]);
        }
      } else if (env.op === "event") {
        if (typeof p.kind === "string") setEvents((prev) => [...prev, p as unknown as CliEvent]);
      } else if (env.op === "exit") {
        setExited({ exitCode: typeof p.exitCode === "number" ? p.exitCode : 0 });
      }
    },
  );
  onCleanup(off);

  return {
    events,
    frames,
    exited,
    reset: () => {
      setEvents([]);
      setFrames([]);
      setExited(null);
    },
    prepend: (evs) => setEvents((prev) => [...prev, ...evs]),
  };
}
